/**
 * @fileoverview PostHog Analytics Provider
 * @module utils/posthog/provider
 *
 * Purpose: PostHog analytics integration for React
 * - Initializes PostHog client with environment configuration
 * - Provides React context for analytics access
 * - Handles client-side analytics initialization
 *
 * Dependencies:
 * - External: posthog-js (PostHog JavaScript SDK)
 * - React: useEffect
 *
 * Used by: app.tsx (wraps entire application)
 */

import posthog from "posthog-js";
import { useEffect, type ReactNode } from "react";

import { PostHogContext } from "./context";

interface PostHogProviderProps {
  children: ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  useEffect(() => {
    const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

    // Production posts through our own origin: CloudFront forwards /_mgn/* to
    // PostHog (ad blockers block *.posthog.com directly). The behaviours live
    // in infra/core-infra/infra/frontend/analyticsProxy.ts (prefix:
    // infra/core-infra/infra/hosts.ts); change both in the SAME commit as this
    // constant. No env var is involved, so
    // the two halves cannot drift apart across deploys.
    // Dev posts directly to PostHog via VITE_PUBLIC_POSTHOG_HOST, so no
    // vite proxy is needed and localhost behaves as before.
    const apiHost = import.meta.env.DEV
      ? import.meta.env.VITE_PUBLIC_POSTHOG_HOST
      : `${window.location.origin}/_mgn`;

    // Check if already initialized (prevents double init in React Strict Mode)
    if (posthog.__loaded) {
      return;
    }

    if (apiKey && apiHost) {
      posthog.init(apiKey, {
        api_host: apiHost,
        // Required with a proxied api_host: keeps toolbar/app links pointed
        // at the real PostHog UI instead of our proxy path.
        ui_host: "https://us.posthog.com",
        person_profiles: "identified_only",
        capture_pageview: false,
        capture_pageleave: true,

        // Session Replay - records user sessions
        session_recording: {
          recordCrossOriginIframes: true,
          maskAllInputs: true,
          maskTextSelector: "[data-private]",
          // Optimize replay data size
          blockClass: "ph-no-capture", // Block elements with this class
        },

        // Web Analytics - performance metrics
        capture_performance: true,

        // Autocapture - automatic event tracking
        autocapture: {
          dom_event_allowlist: ["click", "submit", "change"],
          capture_copied_text: false,
        },

        // Disable verbose console logs in production
        loaded: () => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console -- dev-only init confirmation, gated by DEV
            console.log(
              "PostHog initialized with Session Replay & Web Analytics",
            );
          }
        },
      });
    } else {
      console.warn(
        "PostHog not initialized: Missing VITE_PUBLIC_POSTHOG_KEY" +
          (import.meta.env.DEV ? " or VITE_PUBLIC_POSTHOG_HOST" : ""),
      );
    }
  }, []);

  return (
    <PostHogContext.Provider value={posthog}>
      {children}
    </PostHogContext.Provider>
  );
}
